import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/* Recovery layer — kills the "white screen until hard refresh" problem.
 *
 * 1. AppErrorBoundary: a render-phase crash used to unmount the entire React
 *    tree (blank page). Now it shows a recovery card instead — and because the
 *    usual cause is a tab still running an old bundle after a deploy, it first
 *    checks asset-manifest.json and silently reloads once if a new build is live.
 * 2. UpdateWatcher: detects new deploys in the background (every 3 min + on tab
 *    focus) and refreshes at the next route change, so open tabs never go stale.
 */

const RELOADED_KEY = "sahod-auto-reloaded-for";

function currentMainSrc() {
    const el = document.querySelector('script[src*="/static/js/main"]');
    return el ? el.getAttribute("src") : null;
}

async function newBuildAvailable() {
    try {
        const r = await fetch("/asset-manifest.json", { cache: "no-store" });
        if (!r.ok) return false;
        const m = await r.json();
        const latest = m.files?.["main.js"];
        const running = currentMainSrc();
        return !!latest && !!running && !running.endsWith(latest);
    } catch { return false; }
}

/** Reload once per new build hash — never loops. */
async function reloadIfNewBuild() {
    if (!(await newBuildAvailable())) return false;
    const running = currentMainSrc() || "";
    if (sessionStorage.getItem(RELOADED_KEY) === running) return false;
    sessionStorage.setItem(RELOADED_KEY, running);
    window.location.reload();
    return true;
}

class Boundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
    }
    static getDerivedStateFromError(error) { return { error }; }
    componentDidCatch(error) {
        // Most crashes on navigation are a stale bundle hitting a changed API —
        // a fresh bundle fixes those without the user ever seeing the card.
        reloadIfNewBuild();
        // eslint-disable-next-line no-console
        console.error("[SAHOD] page crashed:", error);
    }
    componentDidUpdate(prev) {
        // Navigating away resets the boundary, so one broken page never bricks the app.
        if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null });
    }
    render() {
        if (!this.state.error) return this.props.children;
        return (
            <div className="min-h-[60vh] flex items-center justify-center p-6">
                <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl shadow-sm p-6 text-center" data-testid="error-recovery-card">
                    <div className="text-4xl mb-2">😵</div>
                    <h1 className="text-lg font-bold text-slate-900">This screen hit a snag</h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Usually this just means a newer version of SAHOD was deployed while this tab was open.
                        Reloading fixes it.
                    </p>
                    <div className="flex gap-2 justify-center mt-4">
                        <button className="h-9 px-4 rounded-md bg-indigo-600 text-white text-sm font-medium" onClick={() => window.location.reload()}>
                            Reload page
                        </button>
                        <button className="h-9 px-4 rounded-md border border-slate-300 text-sm" onClick={() => { window.history.back(); }}>
                            Go back
                        </button>
                    </div>
                    <div className="text-[11px] text-slate-400 mt-3 font-mono truncate">{String(this.state.error?.message || this.state.error)}</div>
                </div>
            </div>
        );
    }
}

export function AppErrorBoundary({ children }) {
    const location = useLocation();
    return <Boundary resetKey={location.pathname}>{children}</Boundary>;
}

export function UpdateWatcher() {
    const location = useLocation();
    const stale = useRef(false);

    useEffect(() => {
        const check = async () => { if (await newBuildAvailable()) stale.current = true; };
        const t = setInterval(check, 3 * 60 * 1000);
        const onVisible = () => { if (!document.hidden) check(); };
        document.addEventListener("visibilitychange", onVisible);
        check();
        return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVisible); };
    }, []);

    // Refresh at a natural boundary (route change) so no in-progress form is lost.
    useEffect(() => {
        if (stale.current) reloadIfNewBuild();
    }, [location.pathname]);

    return null;
}
